import React, { FC, useMemo } from 'react'
import { observer } from 'mobx-react'
import i18n from 'i18next'
import { cartMobx, mallMobx } from 'mobx/mall'
import { decorationMobx, themeMobx } from '@mobxStore'
import { Empty, WaterfallSkeleton } from '@cps'
import ProductItem from '../../ProductItem'
import './index.scss'

interface IProps {
  categories: IMallCategory[];
  onActiveCategoryChange?: (categoryNo: string) => void;
}

const ClassicListTemplate: FC<IProps> = ({ categories, onActiveCategoryChange }) => {
  const sortedCategories = useMemo(() => {
    return categories.map(category => ({
      ...category,
      products: [...category.products].sort((a, b) => {
        const aSort = a.sortInCategory?.[category.no] ?? a.id
        const bSort = b.sortInCategory?.[category.no] ?? b.id
        return bSort - aSort
      })
    }))
  }, [categories])
  const showTabBar = themeMobx.brandTemplateDetails?.globalDecorates?.tabBarSetting?.pagesShow?.mall === 1

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const scrollContainer = e.currentTarget
    const sections = [...scrollContainer.querySelectorAll('.mall-classic-list-category')] as HTMLElement[]
    if (!sections.length) return
    const scrollTop = scrollContainer.scrollTop
    if (scrollTop <= 8) {
      onActiveCategoryChange?.(sections[0].dataset.categoryNo || '')
      return
    }
    if (scrollTop + scrollContainer.clientHeight >= scrollContainer.scrollHeight - 1) {
      onActiveCategoryChange?.(sections[sections.length - 1].dataset.categoryNo || '')
      return
    }
    const containerTop = scrollContainer.getBoundingClientRect().top
    const activeSection = sections.reduce((active, section) => {
      const sectionTop = section.getBoundingClientRect().top - containerTop + scrollTop
      return sectionTop <= scrollTop + 8 ? section : active
    }, sections[0])
    onActiveCategoryChange?.(activeSection.dataset.categoryNo || '')
  }

  if (mallMobx.isLoading) {
    return <WaterfallSkeleton />
  }

  if (!sortedCategories.length) {
    return <Empty msg={i18n.t('mall.noProducts')} height='55vh' />
  }

  return (
    <div className='mall-product-list-content classic-list' onScroll={handleScroll}>
      <div className='mall-classic-list-products'>
        {sortedCategories.map(category => (
          <section
            className='mall-classic-list-category mall-category-anchor'
            id={`mall-category-item-${category.no}`}
            data-category-no={category.no}
            key={category.no}
          >
            <div className='mall-classic-list-category-title'>{category.name}</div>
            <div className='mall-classic-list-category-products'>
              {category.products.map(product => {
                const count = cartMobx.cartResult?.productNoToCntMap?.[product.no] || 0
                return (
                  <ProductItem
                    type='classic-list'
                    decorate={decorationMobx.mallGlobalDecorates || {} as IMallGlobalDecorates}
                    product={product}
                    count={count}
                    lazy={false}
                    backgroundMode={themeMobx.data.backgroundMode}
                    key={`${category.no}-${product.no}`}
                  />
                )
              })}
            </div>
          </section>
        ))}
      </div>
      {showTabBar && <div className='mall-classic-list-tab-fill' />}
    </div>
  )
}

export default observer(ClassicListTemplate)
